// components/common/SearchHeader.tsx
import React, { useState } from "react";
import {
  Search,
  Filter,
  Download,
  ChevronDown,
  ChevronUp,
  X,
} from "lucide-react";
import { SearchHeaderProps } from "../../types";
import { SearchFunction } from "./SearchFunction";

interface SearchHeaderPropsExtended extends SearchHeaderProps {
  adminActivity?: boolean;
  AdminTooltip?: string; // Added prop for selected tooltip type
  adminDirectory?: boolean;
  organizations?: any[];
  customerActivity?: boolean;
  expertActivity?: boolean;
  adminTeams?: boolean;
  activeTab?: string;
  hasPreFilter?: boolean;
  appliedFilters?: any; // Add appliedFilters prop
  AdminTooltipTab?: boolean
  hasTooltipSelectAdmin?: boolean;

  // Add tooltip-specific props
  // tooltipOptions: list of { value, label } to render in the tooltip multi-select dropdown
  tooltipOptions?: { value: string; label: string }[];
  // selectedTooltipTypes: controlled selected values (array of values)
  selectedTooltipTypes?: string[];
  // onTooltipTypeChange: called when selection changes, passes string[] of selected values
  onTooltipTypeChange?: (types: string[]) => void;
  hasTooltipSelect?: boolean;
  // Sales-specific props
  hasSalesFilters?: boolean;
  organizationOptions?: { value: string; label: string }[];
  selectedOrganizations?: string[];
  onOrganizationChange?: (orgs: string[]) => void;
  onDateRangeChange?: (range: { from: string; to: string }) => void;
  onRatingChange?: (rating: string) => void;
  // Package tooltip filters
  hasPackageTooltipFilters?: boolean;
  // Purchases tooltip filters
  hasPurchasesTooltipFilter?: boolean;
  // Package select props
  hasPackageSelect?: boolean;
  packageOptions?: { value: string; label: string }[];
  selectedPackages?: string[];
  onPackageChange?: (packages: string[]) => void;
}

export const SearchHeader: React.FC<SearchHeaderPropsExtended> = ({
  title,
  onSearch,
  onFilter,
  adminActivity = false,
  adminDirectory = false,
  organizations = [],
  customerActivity = false,
  expertActivity = false,
  adminTeams = false,
  activeTab = "",
  hasPreFilter = false,
  appliedFilters = null, // Add appliedFilters default
  // Add tooltip props
  tooltipOptions = [],
  selectedTooltipTypes = [],
  onTooltipTypeChange,
  hasTooltipSelect = false,
  // Sales props
  hasSalesFilters = false,
  organizationOptions = [],
  selectedOrganizations = [],
  onOrganizationChange,
  onDateRangeChange,
  onRatingChange,
  // Package tooltip filters
  hasPackageTooltipFilters = false,
  // Purchases tooltip filters
  hasPurchasesTooltipFilter = false,
  hasTooltipSelectAdmin = false,
  // Package select props
  hasPackageSelect = false,
  packageOptions = [],
  selectedPackages = [],
  onPackageChange,
}) => {
  
  const [showFilter, setShowFilter] = useState(false);
  const [amountRanges, setAmountRanges] = useState({
    range1: false,
    range2: false,
    range3: false,
    range4: false,
    range5: false,
    range6: false,
    custom: false,
  });
  const [status, setStatus] = useState("");
  const [expertRating, setExpertRating] = useState("");
  const [customerRating, setCustomerRating] = useState("");
  const [customMin, setCustomMin] = useState("");
  const [customMax, setCustomMax] = useState("");

  // New filter states for Directory
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [walletRanges, setWalletRanges] = useState({
    range1: false,
    range2: false,
    range3: false,
    range4: false,
    range5: false,
    range6: false,
    custom: false,
  });
  const [walletCustomMin, setWalletCustomMin] = useState("");
  const [walletCustomMax, setWalletCustomMax] = useState("");

  // Section collapse states
  const [sections, setSections] = useState({
    status: true,
    expertRating: true,
    customerRating: true,
    organization: true,
    role: true,
    walletBalance: true,
    amount: true,
    purchasesRating: true,
    salesDateRange: true,
    salesRating: true,
    salesOrganization: true,
    // Package tooltip filter sections
    createdDate: true,
    lastSaleDate: true,
    packageTooltipRating: true,
    sales: true,
    earnings: true,
  });

  // Sales filter states
  const [internalSelectedOrganizations, setInternalSelectedOrganizations] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedRating, setSelectedRating] = useState("");

  // Purchases filter states
  const [purchasesRating, setPurchasesRating] = useState("");

  // Package tooltip filter states
  const [createdDateFrom, setCreatedDateFrom] = useState("");
  const [createdDateTo, setCreatedDateTo] = useState("");
  const [lastSaleDateFrom, setLastSaleDateFrom] = useState("");
  const [lastSaleDateTo, setLastSaleDateTo] = useState("");
  const [packageTooltipRating, setPackageTooltipRating] = useState("");
  const [salesRanges, setSalesRanges] = useState({
    range1: false, // 0-10
    range2: false, // 11-20
    range3: false, // 21-50
    range4: false, // 51-100
    range5: false, // 200+
    custom: false,
  });
  const [salesCustomMin, setSalesCustomMin] = useState("");
  const [salesCustomMax, setSalesCustomMax] = useState("");
  const [earningsRanges, setEarningsRanges] = useState({
    range1: false,
    range2: false,
    range3: false,
    range4: false,
    range5: false,
    range6: false,
    custom: false,
  });
  const [earningsCustomMin, setEarningsCustomMin] = useState("");
  const [earningsCustomMax, setEarningsCustomMax] = useState("");

  // Validation error states
  const [customMaxError, setCustomMaxError] = useState("");
  const [walletCustomMaxError, setWalletCustomMaxError] = useState("");
  const [salesCustomMaxError, setSalesCustomMaxError] = useState("");
  const [earningsCustomMaxError, setEarningsCustomMaxError] = useState("");

  // Validation helper function
  const validateMaxValue = (min: string, max: string): string => {
    if (!max) return "";
    if (!min) return "";
    const minNum = parseFloat(min);
    const maxNum = parseFloat(max);
    if (maxNum <= minNum) {
      return "Max must be greater than Min";
    }
    return "";
  };

  // Check if there are any validation errors
  const hasValidationErrors = (): boolean => {
    return !!(customMaxError || walletCustomMaxError || salesCustomMaxError || earningsCustomMaxError);
  };

  const handleAmountChange = (range: string) => {
    if (range === "custom") {
      // Toggle custom checkbox
      setAmountRanges((prev) => {
        const newCustom = !prev.custom;
        if (newCustom) {
          // If custom is being enabled, unselect all other ranges
          return {
            range1: false,
            range2: false,
            range3: false,
            range4: false,
            range5: false,
            range6: false,
            custom: true,
          };
        } else {
          // If custom is being disabled, just toggle it off and clear custom values
          setCustomMin("");
          setCustomMax("");
          setCustomMaxError("");
          return {
            ...prev,
            custom: false,
          };
        }
      });
    } else {
      // If any other range is selected, unselect custom, clear custom values, and toggle the selected range
      if (amountRanges.custom) {
        setCustomMin("");
        setCustomMax("");
        setCustomMaxError("");
      }
      setAmountRanges((prev) => ({
        ...prev,
        custom: false,
        [range]: !prev[range as keyof typeof prev],
      }));
    }
  };
  const handleWalletChange = (range: string) => {
    if (range === "custom") {
      // Toggle custom checkbox
      setWalletRanges((prev) => {
        const newCustom = !prev.custom;
        if (newCustom) {
          // If custom is being enabled, unselect all other ranges
          return {
            range1: false,
            range2: false,
            range3: false,
            range4: false,
            range5: false,
            range6: false,
            custom: true,
          };
        } else {
          // If custom is being disabled, just toggle it off and clear custom values
          setWalletCustomMin("");
          setWalletCustomMax("");
          setWalletCustomMaxError("");
          return {
            ...prev,
            custom: false,
          };
        }
      });
    } else {
      // If any other range is selected, unselect custom, clear custom values, and toggle the selected range
      if (walletRanges.custom) {
        setWalletCustomMin("");
        setWalletCustomMax("");
        setWalletCustomMaxError("");
      }
      setWalletRanges((prev) => ({
        ...prev,
        custom: false,
        [range]: !prev[range as keyof typeof prev],
      }));
    }
  };
  const handleSalesChange = (range: string) => {
    if (range === "custom") {
      // Toggle custom checkbox
      setSalesRanges((prev) => {
        const newCustom = !prev.custom;
        if (newCustom) {
          // If custom is being enabled, unselect all other ranges
          return {
            range1: false,
            range2: false,
            range3: false,
            range4: false,
            range5: false,
            custom: true,
          };
        } else {
          // If custom is being disabled, just toggle it off and clear custom values
          setSalesCustomMin("");
          setSalesCustomMax("");
          setSalesCustomMaxError("");
          return {
            ...prev,
            custom: false,
          };
        }
      });
    } else {
      // If any other range is selected, unselect custom, clear custom values, and toggle the selected range
      if (salesRanges.custom) {
        setSalesCustomMin("");
        setSalesCustomMax("");
        setSalesCustomMaxError("");
      }
      setSalesRanges((prev) => ({
        ...prev,
        custom: false,
        [range]: !prev[range as keyof typeof prev],
      }));
    }
  };
  const handleEarningsChange = (range: string) => {
    if (range === "custom") {
      // Toggle custom checkbox
      setEarningsRanges((prev) => {
        const newCustom = !prev.custom;
        if (newCustom) {
          // If custom is being enabled, unselect all other ranges
          return {
            range1: false,
            range2: false,
            range3: false,
            range4: false,
            range5: false,
            range6: false,
            custom: true,
          };
        } else {
          // If custom is being disabled, just toggle it off and clear custom values
          setEarningsCustomMin("");
          setEarningsCustomMax("");
          setEarningsCustomMaxError("");
          return {
            ...prev,
            custom: false,
          };
        }
      });
    } else {
      // If any other range is selected, unselect custom, clear custom values, and toggle the selected range
      if (earningsRanges.custom) {
        setEarningsCustomMin("");
        setEarningsCustomMax("");
        setEarningsCustomMaxError("");
      }
      setEarningsRanges((prev) => ({
        ...prev,
        custom: false,
        [range]: !prev[range as keyof typeof prev],
      }));
    }
  };
  const resetFilters = () => {
    setAmountRanges({
      range1: false,
      range2: false,
      range3: false,
      range4: false,
      range5: false,
      range6: false,
      custom: false,
    });
    setStatus("");
    setExpertRating("");
    setCustomerRating("");
    setCustomMin("");
    setCustomMax("");
    setCustomMaxError("");

    // Reset directory-specific filters
    setSelectedOrganization("");
    setSelectedRole("");
    setWalletRanges({
      range1: false,
      range2: false,
      range3: false,
      range4: false,
      range5: false,
      range6: false,
      custom: false,
    });
    setWalletCustomMin("");
    setWalletCustomMax("");
    setWalletCustomMaxError("");

    // Reset sales filters
    setInternalSelectedOrganizations([]);
    setDateFrom("");
    setDateTo("");
    setSelectedRating("");

    // Reset purchases filters
    setPurchasesRating("");

    // Reset package tooltip filters
    setCreatedDateFrom("");
    setCreatedDateTo("");
    setLastSaleDateFrom("");
    setLastSaleDateTo("");
    setPackageTooltipRating("");
    setSalesRanges({
      range1: false,
      range2: false,
      range3: false,
      range4: false,
      range5: false,
      custom: false,
    });
    setSalesCustomMin("");
    setSalesCustomMax("");
    setSalesCustomMaxError("");
    setEarningsRanges({
      range1: false,
      range2: false,
      range3: false,
      range4: false,
      range5: false,
      range6: false,
      custom: false,
    });
    setEarningsCustomMin("");
    setEarningsCustomMax("");
    setEarningsCustomMaxError("");

    // Clear filters in parent component
    if (onFilter) {
      onFilter(null);
    }

    // Clear tooltip multi-select internal state and notify parent
    setInternalSelectedTooltipTypes([]);
    if (onTooltipTypeChange) onTooltipTypeChange([]);

    // Clear package multi-select internal state and notify parent
    setInternalSelectedPackages([]);
    if (onPackageChange) onPackageChange([]);

    // Close the filter popup
    setShowFilter(false);

    // Reset the stored last applied filters to cleared state so reopen shows cleared UI
    lastAppliedFiltersRef.current = {
      amountRanges: {
        range1: false,
        range2: false,
        range3: false,
        range4: false,
        range5: false,
        range6: false,
        custom: false,
      },
      status: "",
      expertRating: "",
      customerRating: "",
      customMin: "",
      customMax: "",
      selectedOrganization: "",
      selectedRole: "",
      walletRanges: {
        range1: false,
        range2: false,
        range3: false,
        range4: false,
        range5: false,
        range6: false,
        custom: false,
      },
      walletCustomMin: "",
      walletCustomMax: "",
      // Sales filters
      selectedOrganizations: [],
      dateFrom: "",
      dateTo: "",
      selectedRating: "",
      // Purchases filters
      purchasesRating: "",
      // Package tooltip filters
      createdDateFrom: "",
      createdDateTo: "",
      lastSaleDateFrom: "",
      lastSaleDateTo: "",
      packageTooltipRating: "",
      salesRanges: {
        range1: false,
        range2: false,
        range3: false,
        range4: false,
        range5: false,
        custom: false,
      },
      salesCustomMin: "",
      salesCustomMax: "",
      earningsRanges: {
        range1: false,
        range2: false,
        range3: false,
        range4: false,
        range5: false,
        range6: false,
        custom: false,
      },
      earningsCustomMin: "",
      earningsCustomMax: "",
    };

    // Call individual callbacks for sales filters to reset parent state
    if (hasSalesFilters) {
      if (onOrganizationChange) {
        onOrganizationChange([]);
      }
      if (onDateRangeChange) {
        onDateRangeChange({ from: "", to: "" });
      }
      if (onRatingChange) {
        onRatingChange("");
      }
    }
  };
  const applyFilters = () => {
    // Create filter object to pass to parent component
    const filters: any = {};

    // Common filters
    if (status && status !== "") filters.status = status;

    // Handle rating filters based on context
    if (expertActivity) {
      // For expert activity, expertRating is customer rating for expert, customerRating is customer rating for expert
      if (expertRating && expertRating !== "")
        filters.expertRating = expertRating;
      if (customerRating && customerRating !== "")
        filters.customerRating = customerRating;
    } else if (adminActivity || customerActivity) {
      // For admin/user activity, use standard rating assignments
      if (expertRating && expertRating !== "")
        filters.expertRating = expertRating;
      if (customerRating && customerRating !== "")
        filters.customerRating = customerRating;
    } // Directory-specific filters
    if (adminDirectory || adminTeams) {
      if (selectedOrganization && selectedOrganization !== "")
        filters.organization = selectedOrganization;

      // Only show role filter for adminDirectory, not for adminTeams
      if (adminDirectory && selectedRole && selectedRole !== "")
        filters.role = selectedRole;

      // Check if any wallet filters are active
      const hasWalletFilters =
        Object.values(walletRanges).some((value) => value === true) ||
        (walletRanges.custom && (walletCustomMin || walletCustomMax));

      if (hasWalletFilters) {
        filters.walletRanges = {
          ...walletRanges,
          customMin: walletCustomMin ? Number(walletCustomMin) : null,
          customMax: walletCustomMax ? Number(walletCustomMax) : null,
        };
      }
    }

    // Check if any amount filters are active
    const hasAmountFilters =
      Object.values(amountRanges).some((value) => value === true) ||
      (amountRanges.custom && (customMin || customMax));

    if (hasAmountFilters) {
      filters.amountRanges = {
        ...amountRanges,
        customMin: customMin ? Number(customMin) : null,
        customMax: customMax ? Number(customMax) : null,
      };
    }

    // Sales-specific filters
    if (hasSalesFilters) {
      if (internalSelectedOrganizations.length > 0) {
        filters.selectedOrganizations = internalSelectedOrganizations;
      }
      if (dateFrom || dateTo) {
        filters.dateRange = {
          from: dateFrom,
          to: dateTo,
        };
      }
      if (selectedRating && selectedRating !== "") {
        filters.rating = selectedRating;
      }
    }

    // Package tooltip filters
    if (hasPackageTooltipFilters) {
      if (createdDateFrom || createdDateTo) {
        filters.createdDateRange = {
          from: createdDateFrom,
          to: createdDateTo,
        };
      }
      if (lastSaleDateFrom || lastSaleDateTo) {
        filters.lastSaleDateRange = {
          from: lastSaleDateFrom,
          to: lastSaleDateTo,
        };
      }
      if (packageTooltipRating && packageTooltipRating !== "") {
        filters.packageTooltipRating = packageTooltipRating;
      }

      // Check if any sales filters are active
      const hasSalesFiltersActive =
        Object.values(salesRanges).some((value) => value === true) ||
        (salesRanges.custom && (salesCustomMin || salesCustomMax));

      if (hasSalesFiltersActive) {
        filters.salesRanges = {
          ...salesRanges,
          customMin: salesCustomMin ? Number(salesCustomMin) : null,
          customMax: salesCustomMax ? Number(salesCustomMax) : null,
        };
      }

      // Check if any earnings filters are active
      const hasEarningsFiltersActive =
        Object.values(earningsRanges).some((value) => value === true) ||
        (earningsRanges.custom && (earningsCustomMin || earningsCustomMax));

      if (hasEarningsFiltersActive) {
        filters.earningsRanges = {
          ...earningsRanges,
          customMin: earningsCustomMin ? Number(earningsCustomMin) : null,
          customMax: earningsCustomMax ? Number(earningsCustomMax) : null,
        };
      }
    }

    // Purchases-specific filters
    if (hasPurchasesTooltipFilter) {
      if (purchasesRating && purchasesRating !== "") {
        filters.purchasesRating = purchasesRating;
      }
      // Amount is already handled above
    }

    // Call the onFilter callback with the filter data
    if (onFilter) {
      onFilter(Object.keys(filters).length > 0 ? filters : null);
    }

    // Call individual callbacks for sales filters
    if (hasSalesFilters) {
      if (onOrganizationChange) {
        onOrganizationChange(internalSelectedOrganizations);
      }
      if (onDateRangeChange) {
        onDateRangeChange({ from: dateFrom, to: dateTo });
      }
      if (onRatingChange) {
        onRatingChange(selectedRating);
      }
    }

    console.log("Applied filters:", filters);
    setShowFilter(false);
    lastAppliedFiltersRef.current = {
      amountRanges,
      status,
      expertRating,
      customerRating,
      customMin,
      customMax,
      selectedOrganization,
      selectedRole,
      walletRanges,
      walletCustomMin,
      walletCustomMax,
      // Sales filters
      selectedOrganizations: internalSelectedOrganizations,
      dateFrom,
      dateTo,
      selectedRating,
      // Purchases filters
      purchasesRating,
      // Package tooltip filters
      createdDateFrom,
      createdDateTo,
      lastSaleDateFrom,
      lastSaleDateTo,
      packageTooltipRating,
      salesRanges,
      salesCustomMin,
      salesCustomMax,
      earningsRanges,
      earningsCustomMin,
      earningsCustomMax,
    };
  };
  const hasActiveFilters = () => {
    const basicFilters =
      status !== "" ||
      expertRating !== "" ||
      customerRating !== "" ||
      Object.values(amountRanges).some((value) => value === true) ||
      (amountRanges.custom && (customMin !== "" || customMax !== ""));

    const salesFiltersActive =
      hasSalesFilters &&
      (internalSelectedOrganizations.length > 0 ||
        dateFrom !== "" ||
        dateTo !== "" ||
        selectedRating !== "");

    const packageTooltipFiltersActive =
      hasPackageTooltipFilters &&
      (createdDateFrom !== "" ||
        createdDateTo !== "" ||
        lastSaleDateFrom !== "" ||
        lastSaleDateTo !== "" ||
        packageTooltipRating !== "" ||
        Object.values(salesRanges).some((value) => value === true) ||
        (salesRanges.custom && (salesCustomMin !== "" || salesCustomMax !== "")) ||
        Object.values(earningsRanges).some((value) => value === true) ||
        (earningsRanges.custom && (earningsCustomMin !== "" || earningsCustomMax !== "")));

    const purchasesFiltersActive =
      hasPurchasesTooltipFilter &&
      (purchasesRating !== "" ||
        Object.values(amountRanges).some((value) => value === true) ||
        (amountRanges.custom && (customMin !== "" || customMax !== "")));

    if (adminDirectory || adminTeams) {
      const walletFiltersActive =
        (activeTab === "customer" || activeTab === "Customer") &&
        (Object.values(walletRanges).some((value) => value === true) ||
          (walletRanges.custom &&
            (walletCustomMin !== "" || walletCustomMax !== "")));

      return (
        basicFilters ||
        selectedOrganization !== "" ||
        (adminDirectory && selectedRole !== "") ||
        walletFiltersActive ||
        salesFiltersActive ||
        packageTooltipFiltersActive ||
        purchasesFiltersActive ||
        hasPreFilter
      );
    }

    return basicFilters || salesFiltersActive || packageTooltipFiltersActive || purchasesFiltersActive || hasPreFilter;
  };

  // Store last applied filter values using useRef to avoid dependency issues
  const lastAppliedFiltersRef = React.useRef({
    amountRanges: {
      range1: false,
      range2: false,
      range3: false,
      range4: false,
      range5: false,
      range6: false,
      custom: false,
    },
    status: "",
    expertRating: "",
    customerRating: "",
    customMin: "",
    customMax: "",
    selectedOrganization: "",
    selectedRole: "",
    walletRanges: {
      range1: false,
      range2: false,
      range3: false,
      range4: false,
      range5: false,
      range6: false,
      custom: false,
    },
    walletCustomMin: "",
    walletCustomMax: "",
    // Sales filters
    selectedOrganizations: [] as string[],
    dateFrom: "",
    dateTo: "",
    selectedRating: "",
    // Purchases filters
    purchasesRating: "",
    // Package tooltip filters
    createdDateFrom: "",
    createdDateTo: "",
    lastSaleDateFrom: "",
    lastSaleDateTo: "",
    packageTooltipRating: "",
    salesRanges: {
      range1: false,
      range2: false,
      range3: false,
      range4: false,
      range5: false,
      custom: false,
    },
    salesCustomMin: "",
    salesCustomMax: "",
    earningsRanges: {
      range1: false,
      range2: false,
      range3: false,
      range4: false,
      range5: false,
      range6: false,
      custom: false,
    },
    earningsCustomMin: "",
    earningsCustomMax: "",
  });

  // When filter popup is opened, set filter states to last applied or appliedFilters
  React.useEffect(() => {
    if (showFilter) {
      if (appliedFilters) {
        // Set filter UI state from appliedFilters if present
        setAmountRanges(
          appliedFilters.amountRanges || {
            range1: false,
            range2: false,
            range3: false,
            range4: false,
            range5: false,
            range6: false,
            custom: false,
          }
        );
        setStatus(appliedFilters.status || "");
        setExpertRating(appliedFilters.expertRating || "");
        setCustomerRating(appliedFilters.customerRating || "");
        setCustomMin(appliedFilters.amountRanges?.customMin?.toString() || "");
        setCustomMax(appliedFilters.amountRanges?.customMax?.toString() || "");
        setSelectedOrganization(appliedFilters.organization || "");
        setSelectedRole(appliedFilters.role || "");
        setWalletRanges(
          appliedFilters.walletRanges || {
            range1: false,
            range2: false,
            range3: false,
            range4: false,
            range5: false,
            range6: false,
            custom: false,
          }
        );
        setWalletCustomMin(
          appliedFilters.walletRanges?.customMin?.toString() || ""
        );
        setWalletCustomMax(
          appliedFilters.walletRanges?.customMax?.toString() || ""
        );
        // Sales filters
        setInternalSelectedOrganizations(appliedFilters.selectedOrganizations || []);
        setDateFrom(appliedFilters.dateRange?.from || "");
        setDateTo(appliedFilters.dateRange?.to || "");
        setSelectedRating(appliedFilters.rating || "");
        // Purchases filters
        setPurchasesRating(appliedFilters.purchasesRating || "");
        // Package tooltip filters
        setCreatedDateFrom(appliedFilters.createdDateRange?.from || "");
        setCreatedDateTo(appliedFilters.createdDateRange?.to || "");
        setLastSaleDateFrom(appliedFilters.lastSaleDateRange?.from || "");
        setLastSaleDateTo(appliedFilters.lastSaleDateRange?.to || "");
        setPackageTooltipRating(appliedFilters.packageTooltipRating || "");
        setSalesRanges(
          appliedFilters.salesRanges || {
            range1: false,
            range2: false,
            range3: false,
            range4: false,
            range5: false,
            custom: false,
          }
        );
        setSalesCustomMin(appliedFilters.salesRanges?.customMin?.toString() || "");
        setSalesCustomMax(appliedFilters.salesRanges?.customMax?.toString() || "");
        setEarningsRanges(
          appliedFilters.earningsRanges || {
            range1: false,
            range2: false,
            range3: false,
            range4: false,
            range5: false,
            range6: false,
            custom: false,
          }
        );
        setEarningsCustomMin(appliedFilters.earningsRanges?.customMin?.toString() || "");
        setEarningsCustomMax(appliedFilters.earningsRanges?.customMax?.toString() || "");
      } else {
        setAmountRanges(lastAppliedFiltersRef.current.amountRanges);
        setStatus(lastAppliedFiltersRef.current.status);
        setExpertRating(lastAppliedFiltersRef.current.expertRating);
        setCustomerRating(lastAppliedFiltersRef.current.customerRating);
        setCustomMin(lastAppliedFiltersRef.current.customMin);
        setCustomMax(lastAppliedFiltersRef.current.customMax);
        setSelectedOrganization(lastAppliedFiltersRef.current.selectedOrganization);
        setSelectedRole(lastAppliedFiltersRef.current.selectedRole);
        setWalletRanges(lastAppliedFiltersRef.current.walletRanges);
        setWalletCustomMin(lastAppliedFiltersRef.current.walletCustomMin);
        setWalletCustomMax(lastAppliedFiltersRef.current.walletCustomMax);
        // Sales filters
        setInternalSelectedOrganizations(lastAppliedFiltersRef.current.selectedOrganizations);
        setDateFrom(lastAppliedFiltersRef.current.dateFrom);
        setDateTo(lastAppliedFiltersRef.current.dateTo);
        setSelectedRating(lastAppliedFiltersRef.current.selectedRating);
        // Purchases filters
        setPurchasesRating(lastAppliedFiltersRef.current.purchasesRating);
        // Package tooltip filters
        setCreatedDateFrom(lastAppliedFiltersRef.current.createdDateFrom);
        setCreatedDateTo(lastAppliedFiltersRef.current.createdDateTo);
        setLastSaleDateFrom(lastAppliedFiltersRef.current.lastSaleDateFrom);
        setLastSaleDateTo(lastAppliedFiltersRef.current.lastSaleDateTo);
        setPackageTooltipRating(lastAppliedFiltersRef.current.packageTooltipRating);
        setSalesRanges(lastAppliedFiltersRef.current.salesRanges);
        setSalesCustomMin(lastAppliedFiltersRef.current.salesCustomMin);
        setSalesCustomMax(lastAppliedFiltersRef.current.salesCustomMax);
        setEarningsRanges(lastAppliedFiltersRef.current.earningsRanges);
        setEarningsCustomMin(lastAppliedFiltersRef.current.earningsCustomMin);
        setEarningsCustomMax(lastAppliedFiltersRef.current.earningsCustomMax);
      }
    }
  }, [showFilter, appliedFilters]);

  // Multi-select tooltip dropdown state
  const [tooltipDropdownOpen, setTooltipDropdownOpen] = React.useState(false);
  // Keep internal selection but respect controlled prop `selectedTooltipTypes`
  const [internalSelectedTooltipTypes, setInternalSelectedTooltipTypes] = React.useState<string[]>(
    selectedTooltipTypes || []
  );
  // Search query for tooltip dropdown
  const [tooltipSearchQuery, setTooltipSearchQuery] = React.useState('');

  // Multi-select package dropdown state
  const [packageDropdownOpen, setPackageDropdownOpen] = React.useState(false);
  // Keep internal selection but respect controlled prop `selectedPackages`
  const [internalSelectedPackages, setInternalSelectedPackages] = React.useState<string[]>(
    selectedPackages || []
  );
  // Search query for package dropdown
  const [packageSearchQuery, setPackageSearchQuery] = React.useState('');

  // Sync internal state when parent props change
  React.useEffect(() => {
    if (Array.isArray(selectedTooltipTypes)) {
      // Only update if values actually differ to prevent infinite loops
      const hasChanged = 
        selectedTooltipTypes.length !== internalSelectedTooltipTypes.length ||
        selectedTooltipTypes.some((type, idx) => type !== internalSelectedTooltipTypes[idx]);
      
      if (hasChanged) {
        setInternalSelectedTooltipTypes(selectedTooltipTypes);
      }
    }
  }, [selectedTooltipTypes, internalSelectedTooltipTypes]);

  React.useEffect(() => {
    if (Array.isArray(selectedPackages)) {
      // Only update if values actually differ to prevent infinite loops
      const hasChanged = 
        selectedPackages.length !== internalSelectedPackages.length ||
        selectedPackages.some((pkg, idx) => pkg !== internalSelectedPackages[idx]);
      
      if (hasChanged) {
        setInternalSelectedPackages(selectedPackages);
      }
    }
  }, [selectedPackages, internalSelectedPackages]);

  React.useEffect(() => {
    if (Array.isArray(selectedOrganizations)) {
      // Only update if values actually differ to prevent infinite loops
      const hasChanged = 
        selectedOrganizations.length !== internalSelectedOrganizations.length ||
        selectedOrganizations.some((org, idx) => org !== internalSelectedOrganizations[idx]);
      
      if (hasChanged) {
        setInternalSelectedOrganizations(selectedOrganizations);
      }
    }
  }, [selectedOrganizations, internalSelectedOrganizations]);

  // Multi-select organization dropdown state for sales
  const [organizationDropdownOpen, setOrganizationDropdownOpen] = React.useState(false);

  // Close dropdown on outside click
  React.useEffect(() => {
    if (!tooltipDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      const dropdown = document.getElementById("tooltip-dropdown");
      if (dropdown && !dropdown.contains(e.target as Node)) {
        setTooltipDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [tooltipDropdownOpen]);

  // Close package dropdown on outside click
  React.useEffect(() => {
    if (!packageDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      const dropdown = document.getElementById("package-dropdown");
      if (dropdown && !dropdown.contains(e.target as Node)) {
        setPackageDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [packageDropdownOpen]);

  // Handle checkbox change
  const handleTooltipCheckbox = (type: string) => {
    setInternalSelectedTooltipTypes((prev) => {
      let next: string[];
      if (prev.includes(type)) {
        next = prev.filter((t) => t !== type);
      } else {
        next = [...prev, type];
      }
      // notify parent
      if (onTooltipTypeChange) onTooltipTypeChange(next);
      return next;
    });
  };

  // Handle package checkbox change
  const handlePackageCheckbox = (packageId: string) => {
    setInternalSelectedPackages((prev) => {
      let next: string[];
      if (prev.includes(packageId)) {
        next = prev.filter((p) => p !== packageId);
      } else {
        next = [...prev, packageId];
      }
      // notify parent
      if (onPackageChange) onPackageChange(next);
      return next;
    });
  };

  // Close organization dropdown on outside click
  React.useEffect(() => {
    if (!organizationDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      const dropdown = document.getElementById("organization-dropdown");
      if (dropdown && !dropdown.contains(e.target as Node)) {
        setOrganizationDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [organizationDropdownOpen]);

  // Handle organization checkbox change
  const handleOrganizationCheckbox = (org: string) => {
    setInternalSelectedOrganizations((prev) => {
      let next: string[];
      if (prev.includes(org)) {
        next = prev.filter((o) => o !== org);
      } else {
        next = [...prev, org];
      }
      return next;
    });
  };


  return (
    <div className="flex justify-between items-center mb-2 mt-2">
      <h1 className="text-[20px] nunito-semibold text-[#696969]">{title}</h1>
      <div className=" relative flex gap-4">
        {/* Tooltip Type Multi-Select Dropdown */}
        {hasTooltipSelect && (
          <div className="relative " id="tooltip-dropdown">
            <div
              className="h-[40px] w-[280px] px-3 pr-8 border border-[#EBEBEB] rounded-[8px] text-[16px] nunito-regular text-[#919191] flex items-center bg-white cursor-pointer"
              onClick={() => setTooltipDropdownOpen((open) => !open)}
            >
              <span className="truncate" >
                {internalSelectedTooltipTypes.length === 0
                  ? "Select Package"
                  : // show labels for selected values
                    (tooltipOptions.length > 0
                      ? internalSelectedTooltipTypes
                          .map((val) => tooltipOptions.find((o) => o.value === val)?.label || val)
                          .join(", ")
                      : internalSelectedTooltipTypes.join(", "))}
              </span>
              {/* Use ChevronDownUp here */}
               {tooltipDropdownOpen ? (
      <ChevronUp className="absolute right-2 top-2.5 h-[24px] w-[24px] text-[#919191] pointer-events-none" />
    ) : (
      <ChevronDown className="absolute right-2 top-2.5 h-[24px] w-[24px] text-[#919191] pointer-events-none" />
    )}
            </div>
            {tooltipDropdownOpen && (
   <div className="absolute left-0 mt-2 w-[280px] max-h-[500px] overflow-y-auto text-[#696969] text-[16px] nunito-semibold bg-white border border-[#EBEBEB] rounded-lg shadow-lg z-50">
               <SearchFunction onSearch={setTooltipSearchQuery} placeholder="Search tooltips..." />
               {/* Render provided tooltipOptions if available, otherwise fall back to static items */}
                {(() => {
                  const filteredTooltipOptions = tooltipOptions.filter(opt => 
                    opt.label.toLowerCase().includes(tooltipSearchQuery.toLowerCase())
                  );
                  return filteredTooltipOptions && filteredTooltipOptions.length > 0 ? (
                    filteredTooltipOptions.map((opt) => (
                      <label key={opt.value} className="flex items-center px-3 py-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={internalSelectedTooltipTypes.includes(opt.value)}
                          onChange={() => handleTooltipCheckbox(opt.value)}
                          className="mr-2 accent-[#F15D18]"
                        />
                        {opt.label}
                      </label>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-[#919191]">No tooltips found</div>
                  );
                })()}
              </div>
            )}
          </div>
        )}
        {hasTooltipSelectAdmin && (
          <div className="relative " id="package-dropdown">
            <div
              className="h-[40px]  w-[280px] px-3 pr-8 border border-[#EBEBEB] rounded-[8px] text-[16px] nunito-regular text-[#919191] flex items-center bg-white cursor-pointer"
              onClick={() => setPackageDropdownOpen((open) => !open)}
            >
              <span className="truncate" >
                {internalSelectedPackages.length === 0
                  ? "Select Package"
                  : // show labels for selected values
                    (packageOptions.length > 0
                      ? internalSelectedPackages
                          .map((val) => packageOptions.find((o) => o.value === val)?.label || val)
                          .join(", ")
                      : internalSelectedPackages.join(", "))}
              </span>
              {/* Use ChevronDownUp here */}
               {packageDropdownOpen ? (
      <ChevronUp className="absolute right-2 top-2.5 h-[24px] w-[24px] text-[#919191] pointer-events-none" />
    ) : (
      <ChevronDown className="absolute right-2 top-2.5 h-[24px] w-[24px] text-[#919191] pointer-events-none" />
    )}
            </div>
            {packageDropdownOpen && (
              <div className="absolute left-0 mt-2 w-[280px] max-h-[500px] overflow-y-auto text-[#696969] text-[16px] nunito-semibold bg-white border border-[#EBEBEB] rounded-lg shadow-lg z-50">
                <SearchFunction onSearch={setPackageSearchQuery} placeholder="Search packages..." />
                {/* Render provided packageOptions if available, otherwise fall back to static items */}
                {(() => {
                  const filteredPackageOptions = packageOptions.filter(opt => 
                    opt.label.toLowerCase().includes(packageSearchQuery.toLowerCase())
                  );
                  return filteredPackageOptions && filteredPackageOptions.length > 0 ? (
                    filteredPackageOptions.map((opt) => (
                      <label key={opt.value} className="flex items-center px-3 py-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={internalSelectedPackages.includes(opt.value)}
                          onChange={() => handlePackageCheckbox(opt.value)}
                          className="mr-2 accent-[#F15D18]"
                        />
                        {opt.label}
                      </label>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-[#919191]">No packages found</div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-[24px] w-[24px] text-gray-400" />
          <input
            type="text"
            placeholder={adminActivity ? "Search by Expert" : "Search by Name"}
            onChange={(e) => onSearch(e.target.value)}
            className="pl-10 pr-4 h-[40px] w-[215px] py-2 border rounded-lg border-[#EBEBEB] text-[16px] nunito-regular text-[#919191]  focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div>
          <button
            className={`p-2 border border-[#EBEBEB] rounded-lg ${
              hasActiveFilters()
                ? "bg-[#F15D18]"
                : showFilter
                ? "bg-[#EBEBEB]"
                : ""
            }`}
            onClick={() => setShowFilter(!showFilter)}
          >
            <Filter
              className={`h-[24px] w-[24px] ${
                hasActiveFilters() ? "text-white" : "text-[#919191]"
              }`}
            />
          </button>
        </div>
        <div className="relative group">
          <button className="p-2 border border-[#EBEBEB] rounded-lg">
            <Download className="h-[24px] w-[24px] text-[#919191]" />
          </button>
          <div className="absolute right-0 -top-8 px-2 py-1 bg-[#FAFAFA] nunito-regular text-[12px] text-[#696969] text-xs rounded-[8px] shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-30" dir="rtl">
            !This feature is on its way - stay tuned
          </div>
        </div>
        {/* Filter Popup */}
        {showFilter && (
          <div className="absolute right-0 top-12 z-20">
            <div className="w-[392px]  p-4 rounded-[16px]   max-h-[67vh] overflow-y-auto border border-[#BCBCBC] bg-white ">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <Filter className="mr-2 w-[24px] h-[24px]" />
                  <h2 className="text-[20px] text-[#0C0C0C] nunito-semibold">
                    Filters
                  </h2>
                </div>
                <button type="button" onClick={resetFilters} aria-label="Reset all filters">
                  <p className="text-[#0C0C0C] text-[16px] nunito-semibold">Reset all</p>
                </button>
              </div>{" "}
              <div className="overflow-y-auto"> 

              {/* Status Filter - Only for Admin and Customer Activity */}
              {(adminActivity || customerActivity || expertActivity) && (
                <>
                  <div className="mb-5">
                    <div
                      className="flex justify-between items-center mb-2 cursor-pointer"
                      onClick={() =>
                        setSections({ ...sections, status: !sections.status })
                      }
                    >
                      <h3 className="text-[#404040] text-[16px] nunito-semibold">
                        Status
                      </h3>
                      {sections.status ? (
                        <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                      ) : (
                        <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                      )}
                    </div>
                    {sections.status && (
                      <div className="relative">
                        <select
                          className="w-full h-[40px] p-2 border-[1px] border-[#A8A8A8] rounded-[8px] text-[16px] appearance-none nunito-semibold text-[#696969]"
                          value={status}
                          onChange={(e) => setStatus(e.target.value)}
                        >
                          <option value="">All Status</option>
                          <option value="completed">Completed</option>
                          <option value="Issue reported">Issue Reported</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <ChevronDown className="text-[#A8A8A8] w-[24px] h-[24px]" />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="border-b  mb-5 border-[#DFDFDF]"></div>
                </>
              )}{" "}
              {/* Expert Rating Filter - Only for Admin and Customer Activity */}
              {(adminActivity || customerActivity) && (
                <>
                  <div className="mb-6">
                    <div
                      className="flex justify-between items-center mb-2 cursor-pointer"
                      onClick={() =>
                        setSections({
                          ...sections,
                          expertRating: !sections.expertRating,
                        })
                      }
                    >
                      <h3 className="text-[#404040] text-[16px] nunito-semibold">
                        Expert Rating
                      </h3>
                      {sections.expertRating ? (
                        <ChevronUp className="text-[#404040] w-[24px] h-[24px]" />
                      ) : (
                        <ChevronDown className="text-[#404040] w-[24px] h-[24px]" />
                      )}
                    </div>
                    {sections.expertRating && (
                      <div className="relative">
                        <select
                          className="w-full h-[40px] p-2 border-[1px] border-[#A8A8A8] rounded-[8px] text-[16px] appearance-none nunito-semibold text-[#696969]"
                          value={expertRating}
                          onChange={(e) => setExpertRating(e.target.value)}
                        >
                          <option value="">Select Rating</option>
                          <option value="1">1 Star</option>
                          <option value="2">2 Stars</option>
                          <option value="3">3 Stars</option>
                          <option value="4">4 Stars</option>
                          <option value="5">5 Stars</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <ChevronDown className="text-[#A8A8A8] w-[24px] h-[24px]" />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="border-b mb-5 border-[#DFDFDF]"></div>
                </>
              )}{" "}
              {/* Customer Rating Filter - Only for Admin Activity and Expert Activity */}
              {(adminActivity || expertActivity) && (
                <>
                  <div className="mb-6">
                    <div
                      className="flex justify-between items-center mb-2 cursor-pointer"
                      onClick={() =>
                        setSections({
                          ...sections,
                          customerRating: !sections.customerRating,
                        })
                      }
                    >
                      <h3 className="text-[#404040] text-[16px] nunito-semibold">
                        Customer Rating
                      </h3>
                      {sections.customerRating ? (
                        <ChevronUp className="text-[#404040] w-[24px] h-[24px]" />
                      ) : (
                        <ChevronDown className="text-[#404040] w-[24px] h-[24px]" />
                      )}
                    </div>
                    {sections.customerRating && (
                      <div className="relative">
                        <select
                          className="w-full h-[40px] p-2 border-[1px] border-[#A8A8A8] rounded-[8px] text-[16px] appearance-none nunito-semibold text-[#696969]"
                          value={customerRating}
                          onChange={(e) => setCustomerRating(e.target.value)}
                        >
                          <option value="">Select Rating</option>
                          <option value="1">1 Star</option>
                          <option value="2">2 Stars</option>
                          <option value="3">3 Stars</option>
                          <option value="4">4 Stars</option>
                          <option value="5">5 Stars</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <ChevronDown className="text-[#A8A8A8] w-[24px] h-[24px]" />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="border-b mb-5 border-[#DFDFDF]"></div>
                </>
              )}{" "}
              {/* Teams-specific filters */}
              {adminTeams && (
                <>
                  {/* Organization Filter */}
                  <div className="mb-5">
                    <div
                      className="flex justify-between items-center mb-2 cursor-pointer"
                      onClick={() =>
                        setSections({
                          ...sections,
                          organization: !sections.organization,
                        })
                      }
                    >
                      <h3 className="text-[#404040] text-[16px] nunito-semibold">
                        Organization
                      </h3>
                      {sections.organization ? (
                        <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                      ) : (
                        <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                      )}
                    </div>
                    {sections.organization && (
                      <div className="relative">
                        <select
                          className="w-full h-[40px] p-2 border-[1px] border-[#A8A8A8] rounded-[8px] text-[16px] appearance-none nunito-semibold text-[#696969]"
                          value={selectedOrganization}
                          onChange={(e) =>
                            setSelectedOrganization(e.target.value)
                          }
                        >
                          <option value="">All Organizations</option>
                          {organizations.map((org) => (
                            <option key={org.id} value={org.id}>
                              {org.name}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <ChevronDown className="text-[#A8A8A8] w-[24px] h-[24px]" />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="border-b mb-5 border-[#DFDFDF]"></div>{" "}
                  {/* Wallet Balance Filter - Only for Customer tab */}
                  {activeTab === "Customer" && (
                    <>
                      <div className="mb-6">
                        <div
                          className="flex justify-between items-center mb-3 cursor-pointer"
                          onClick={() =>
                            setSections({
                              ...sections,
                              walletBalance: !sections.walletBalance,
                            })
                          }
                        >
                          <h3 className="text-[#404040] text-[16px] nunito-semibold">
                            Wallet Balance
                          </h3>
                          {sections.walletBalance ? (
                            <ChevronUp className="text-[#404040] w-[24px] h-[24px]" />
                          ) : (
                            <ChevronDown className="text-[#404040] w-[24px] h-[24px]" />
                          )}
                        </div>{" "}
                        {sections.walletBalance && (
                          <>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id="wallet1"
                                  className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                                  checked={walletRanges.range1}
                                  onChange={() => handleWalletChange("range1")}
                                />
                                <label
                                  htmlFor="wallet1"
                                  className="text-[#4F4F4F] text-[14px] nunito-regular"
                                >
                                  $ 0-100
                                </label>
                              </div>
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id="wallet2"
                                  className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                                  checked={walletRanges.range2}
                                  onChange={() => handleWalletChange("range2")}
                                />
                                <label
                                  htmlFor="wallet2"
                                  className="text-[#4F4F4F] text-[14px] nunito-regular"
                                >
                                  $ 101-200
                                </label>
                              </div>
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id="wallet3"
                                  className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                                  checked={walletRanges.range3}
                                  onChange={() => handleWalletChange("range3")}
                                />
                                <label
                                  htmlFor="wallet3"
                                  className="text-[#4F4F4F] text-[14px] nunito-regular"
                                >
                                  $ 201-300
                                </label>
                              </div>
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id="wallet4"
                                  className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                                  checked={walletRanges.range4}
                                  onChange={() => handleWalletChange("range4")}
                                />
                                <label
                                  htmlFor="wallet4"
                                  className="text-[#4F4F4F] text-[14px] nunito-regular"
                                >
                                  $ 301-400
                                </label>
                              </div>
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id="wallet5"
                                  className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                                  checked={walletRanges.range5}
                                  onChange={() => handleWalletChange("range5")}
                                />
                                <label
                                  htmlFor="wallet5"
                                  className="text-[#4F4F4F] text-[14px] nunito-regular"
                                >
                                  $ 401-500
                                </label>
                              </div>
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id="wallet6"
                                  className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                                  checked={walletRanges.range6}
                                  onChange={() => handleWalletChange("range6")}
                                />
                                <label
                                  htmlFor="wallet6"
                                  className="text-[#4F4F4F] text-[14px] nunito-regular"
                                >
                                  $ 501+
                                </label>
                              </div>
                            </div>

                            <div className="mb-4">
                              <div className="flex items-center mb-2">
                                <input
                                  type="checkbox"
                                  id="walletCustom"
                                  className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                                  checked={walletRanges.custom}
                                  onChange={() => handleWalletChange("custom")}
                                />
                                <label
                                  htmlFor="walletCustom"
                                  className="text-[#000000] text-[14px] nunito-regular"
                                >
                                  Custom
                                </label>
                              </div>

                              {walletRanges.custom && (
                                <div className="flex gap-2">
                                  <div className="flex-1">
                                    <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                                      Min
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="Enter Amount"
                                      value={walletCustomMin}
                                      onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, "");
                                        setWalletCustomMin(value);
                                        // Validate max when min changes
                                        if (walletCustomMax) {
                                          setWalletCustomMaxError(validateMaxValue(value, walletCustomMax));
                                        }
                                      }}
                                      className="w-full h-[49px] p-2 border border-[#D9D9D9] rounded-[8px] text-[14px] text-[#404040]"
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                                      Max
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="Enter Amount"
                                      value={walletCustomMax}
                                      onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, "");
                                        setWalletCustomMax(value);
                                        setWalletCustomMaxError(validateMaxValue(walletCustomMin, value));
                                      }}
                                      className={`w-full h-[49px] p-2 border rounded-[8px] text-[14px] text-[#404040] ${
                                        walletCustomMaxError ? 'border-red-500' : 'border-[#D9D9D9]'
                                      }`}
                                    />
                                    {walletCustomMaxError && (
                                      <p className="text-red-500 text-[12px] mt-1">{walletCustomMaxError}</p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      <div className="border-b mb-5 border-[#DFDFDF]"></div>
                    </>
                  )}
                </>
              )}{" "}
              {/* Directory-specific filters */}
              {adminDirectory && (
                <>
                  {/* Organization Filter */}
                  <div className="mb-5">
                    <div
                      className="flex justify-between items-center mb-2 cursor-pointer"
                      onClick={() =>
                        setSections({
                          ...sections,
                          organization: !sections.organization,
                        })
                      }
                    >
                      <h3 className="text-[#404040] text-[16px] nunito-semibold">
                        Organization
                      </h3>
                      {sections.organization ? (
                        <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                      ) : (
                        <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                      )}
                    </div>
                    {sections.organization && (
                      <div className="relative">
                        <select
                          className="w-full h-[40px] p-2 border-[1px] border-[#A8A8A8] rounded-[8px] text-[16px] appearance-none nunito-semibold text-[#696969]"
                          value={selectedOrganization}
                          onChange={(e) =>
                            setSelectedOrganization(e.target.value)
                          }
                        >
                          <option value="">All Organizations</option>
                          {organizations.map((org) => (
                            <option key={org.id} value={org.id}>
                              {org.name}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <ChevronDown className="text-[#A8A8A8] w-[24px] h-[24px]" />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="border-b mb-5 border-[#DFDFDF]"></div>
                  {/* Role Filter */}
                  <div className="mb-5">
                    <div
                      className="flex justify-between items-center mb-2 cursor-pointer"
                      onClick={() =>
                        setSections({ ...sections, role: !sections.role })
                      }
                    >
                      <h3 className="text-[#404040] text-[16px] nunito-semibold">
                        Role
                      </h3>
                      {sections.role ? (
                        <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                      ) : (
                        <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                      )}
                    </div>
                    {sections.role && (
                      <div className="relative">
                        {" "}
                        <select
                          className="w-full h-[40px] p-2 border-[1px] border-[#A8A8A8] rounded-[8px] text-[16px] appearance-none nunito-semibold text-[#696969]"
                          value={selectedRole}
                          onChange={(e) => setSelectedRole(e.target.value)}
                        >
                          <option value="">All Roles</option>
                          {activeTab === "Customer" ||
                          activeTab === "customer" ? (
                            <>
                              <option value="customer_owner">Team Owner</option>
                              <option value="customer_team_lead">
                                Team Lead
                              </option>
                              <option value="customer_team_member">
                                Team Member
                              </option>
                            </>
                          ) : (
                            <>
                              <option value="expert_owner">Team Owner</option>
                              <option value="expert_team_lead">
                                Team Lead
                              </option>
                              <option value="expert_team_member">
                                Team Member
                              </option>
                            </>
                          )}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <ChevronDown className="text-[#A8A8A8] w-[24px] h-[24px]" />
                        </div>
                      </div>
                    )}
                  </div>{" "}
                  <div className="border-b mb-5 border-[#DFDFDF]"></div>{" "}
                  {/* Wallet Balance Filter - Only for Customer tab in adminDirectory */}
                  {(activeTab === "customer" || activeTab === "Customer") && (
                    <>
                      <div className="mb-6">
                        <div
                          className="flex justify-between items-center mb-3 cursor-pointer"
                          onClick={() =>
                            setSections({
                              ...sections,
                              walletBalance: !sections.walletBalance,
                            })
                          }
                        >
                          <h3 className="text-[#404040] text-[16px] nunito-semibold">
                            Wallet Balance
                          </h3>
                          {sections.walletBalance ? (
                            <ChevronUp className="text-[#404040] w-[24px] h-[24px]" />
                          ) : (
                            <ChevronDown className="text-[#404040] w-[24px] h-[24px]" />
                          )}
                        </div>{" "}
                        {sections.walletBalance && (
                          <>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id="wallet1"
                                  className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                                  checked={walletRanges.range1}
                                  onChange={() => handleWalletChange("range1")}
                                />
                                <label
                                  htmlFor="wallet1"
                                  className="text-[#4F4F4F] text-[14px] nunito-regular"
                                >
                                  $ 0-100
                                </label>
                              </div>
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id="wallet2"
                                  className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                                  checked={walletRanges.range2}
                                  onChange={() => handleWalletChange("range2")}
                                />
                                <label
                                  htmlFor="wallet2"
                                  className="text-[#4F4F4F] text-[14px] nunito-regular"
                                >
                                  $ 101-200
                                </label>
                              </div>
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id="wallet3"
                                  className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                                  checked={walletRanges.range3}
                                  onChange={() => handleWalletChange("range3")}
                                />
                                <label
                                  htmlFor="wallet3"
                                  className="text-[#4F4F4F] text-[14px] nunito-regular"
                                >
                                  $ 201-300
                                </label>
                              </div>
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id="wallet4"
                                  className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                                  checked={walletRanges.range4}
                                  onChange={() => handleWalletChange("range4")}
                                />
                                <label
                                  htmlFor="wallet4"
                                  className="text-[#4F4F4F] text-[14px] nunito-regular"
                                >
                                  $ 301-400
                                </label>
                              </div>
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id="wallet5"
                                  className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                                  checked={walletRanges.range5}
                                  onChange={() => handleWalletChange("range5")}
                                />
                                <label
                                  htmlFor="wallet5"
                                  className="text-[#4F4F4F] text-[14px] nunito-regular"
                                >
                                  $ 401-500
                                </label>
                              </div>
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id="wallet6"
                                  className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                                  checked={walletRanges.range6}
                                  onChange={() => handleWalletChange("range6")}
                                />
                                <label
                                  htmlFor="wallet6"
                                  className="text-[#4F4F4F] text-[14px] nunito-regular"
                                >
                                  $ 501+
                                </label>
                              </div>
                            </div>

                            <div className="mb-4">
                              <div className="flex items-center mb-2">
                                <input
                                  type="checkbox"
                                  id="walletCustom"
                                  className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                                  checked={walletRanges.custom}
                                  onChange={() => handleWalletChange("custom")}
                                />
                                <label
                                  htmlFor="walletCustom"
                                  className="text-[#000000] text-[14px] nunito-regular"
                                >
                                  Custom
                                </label>
                              </div>

                              {walletRanges.custom && (
                                <div className="flex gap-2">
                                  <div className="flex-1">
                                    <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                                      Min
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="Enter Amount"
                                      value={walletCustomMin}
                                      onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, "");
                                        setWalletCustomMin(value);
                                        // Validate max when min changes
                                        if (walletCustomMax) {
                                          setWalletCustomMaxError(validateMaxValue(value, walletCustomMax));
                                        }
                                      }}
                                      className="w-full h-[49px] p-2 border border-[#D9D9D9] rounded-[8px] text-[14px] text-[#404040]"
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                                      Max
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="Enter Amount"
                                      value={walletCustomMax}
                                      onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, "");
                                        setWalletCustomMax(value);
                                        setWalletCustomMaxError(validateMaxValue(walletCustomMin, value));
                                      }}
                                      className={`w-full h-[49px] p-2 border rounded-[8px] text-[14px] text-[#404040] ${
                                        walletCustomMaxError ? 'border-red-500' : 'border-[#D9D9D9]'
                                      }`}
                                    />
                                    {walletCustomMaxError && (
                                      <p className="text-red-500 text-[12px] mt-1">{walletCustomMaxError}</p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      <div className="border-b mb-5 border-[#DFDFDF]"></div>
                    </>
                  )}
                </>
              )}

              {/* Amount Filter - Show as Amount for Customer tab, Total Earnings for Expert tab */}
              {adminTeams && (
                <div className="mb-6">
                  <div
                    className="flex justify-between items-center mb-3 cursor-pointer"
                    onClick={() =>
                      setSections({ ...sections, amount: !sections.amount })
                    }
                  >
                    <h3 className="text-[#404040] text-[16px] nunito-semibold">
                      {activeTab === "Expert" ? "Total Earnings" : "Amount"}
                    </h3>
                    {sections.amount ? (
                      <ChevronUp className="text-[#404040] w-[24px] h-[24px]" />
                    ) : (
                      <ChevronDown className="text-[#404040] w-[24px] h-[24px]" />
                    )}
                  </div>{" "}
                  {sections.amount && (
                    <>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="amount1"
                            className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                            checked={amountRanges.range1}
                            onChange={() => handleAmountChange("range1")}
                          />
                          <label
                            htmlFor="amount1"
                            className="text-[#4F4F4F] text-[14px] nunito-regular"
                          >
                            $ 0-100
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="amount2"
                            className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                            checked={amountRanges.range2}
                            onChange={() => handleAmountChange("range2")}
                          />
                          <label
                            htmlFor="amount2"
                            className="text-[#4F4F4F] text-[14px] nunito-regular"
                          >
                            $ 101-200
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="amount3"
                            className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                            checked={amountRanges.range3}
                            onChange={() => handleAmountChange("range3")}
                          />
                          <label
                            htmlFor="amount3"
                            className="text-[#4F4F4F] text-[14px] nunito-regular"
                          >
                            $ 201-300
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="amount4"
                            className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                            checked={amountRanges.range4}
                            onChange={() => handleAmountChange("range4")}
                          />
                          <label
                            htmlFor="amount4"
                            className="text-[#4F4F4F] text-[14px] nunito-regular"
                          >
                            $ 301-400
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="amount5"
                            className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                            checked={amountRanges.range5}
                            onChange={() => handleAmountChange("range5")}
                          />
                          <label
                            htmlFor="amount5"
                            className="text-[#4F4F4F] text-[14px] nunito-regular"
                          >
                            $ 401-500
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="amount6"
                            className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                            checked={amountRanges.range6}
                            onChange={() => handleAmountChange("range6")}
                          />
                          <label
                            htmlFor="amount6"
                            className="text-[#4F4F4F] text-[14px] nunito-regular"
                          >
                            $ 501+
                          </label>
                        </div>
                      </div>

                      <div className="mb-4">
                        <div className="flex items-center mb-4">
                          <input
                            type="checkbox"
                            id="custom"
                            className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                            checked={amountRanges.custom}
                            onChange={() => handleAmountChange("custom")}
                          />
                          <label
                            htmlFor="custom"
                            className="text-[#000000] text-[14px] nunito-regular"
                          >
                            Custom
                          </label>
                        </div>

                        {amountRanges.custom && (
                          <div className="flex gap-2">
                            <div className="flex-1">
                              <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                                Min
                              </label>
                              <input
                                type="text"
                                placeholder="Enter Amount"
                                value={customMin}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/[^0-9]/g, "");
                                  setCustomMin(value);
                                  // Validate max when min changes
                                  if (customMax) {
                                    setCustomMaxError(validateMaxValue(value, customMax));
                                  }
                                }}
                                className="w-full h-[49px] p-2 border border-[#D9D9D9] rounded-[8px] text-[14px] text-[#404040]"
                              />
                            </div>
                            <div className="flex-1">
                              <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                                Max
                              </label>
                              <input
                                type="text"
                                placeholder="Enter Amount"
                                value={customMax}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/[^0-9]/g, "");
                                  setCustomMax(value);
                                  setCustomMaxError(validateMaxValue(customMin, value));
                                }}
                                className={`w-full h-[49px] p-2 border rounded-[8px] text-[14px] text-[#404040] ${
                                  customMaxError ? 'border-red-500' : 'border-[#D9D9D9]'
                                }`}
                              />
                              {customMaxError && (
                                <p className="text-red-500 text-[12px] mt-1">{customMaxError}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  <div className="border-b mb-5 border-[#DFDFDF]"></div>
                </div>
              )}{" "}
              {/* Amount Filter - For other contexts */}
              {(adminDirectory ||
                adminActivity ||
                expertActivity ||
                customerActivity) && (
                <div className="mb-6">
                  <div
                    className="flex justify-between items-center mb-3 cursor-pointer"
                    onClick={() =>
                      setSections({ ...sections, amount: !sections.amount })
                    }
                  >
                    {" "}
                    <h3 className="text-[#404040] text-[16px] nunito-semibold">
                      {adminActivity || expertActivity
                        ? "Earnings"
                        : adminDirectory
                        ? activeTab === "customer"
                          ? "Total Amount"
                          : "Total Earnings"
                        : "Amount"}
                    </h3>
                    {sections.amount ? (
                      <ChevronUp className="text-[#404040] w-[24px] h-[24px]" />
                    ) : (
                      <ChevronDown className="text-[#404040] w-[24px] h-[24px]" />
                    )}
                  </div>{" "}

                  {sections.amount && (
                    <>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="amount1"
                            className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                            checked={amountRanges.range1}
                            onChange={() => handleAmountChange("range1")}
                          />
                          <label
                            htmlFor="amount1"
                            className="text-[#4F4F4F] text-[14px] nunito-regular"
                          >
                            $ 0-100
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="amount2"
                            className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                            checked={amountRanges.range2}
                            onChange={() => handleAmountChange("range2")}
                          />
                          <label
                            htmlFor="amount2"
                            className="text-[#4F4F4F] text-[14px] nunito-regular"
                          >
                            $ 101-200
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="amount3"
                            className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                            checked={amountRanges.range3}
                            onChange={() => handleAmountChange("range3")}
                          />
                          <label
                            htmlFor="amount3"
                            className="text-[#4F4F4F] text-[14px] nunito-regular"
                          >
                            $ 201-300
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="amount4"
                            className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                            checked={amountRanges.range4}
                            onChange={() => handleAmountChange("range4")}
                          />
                          <label
                            htmlFor="amount4"
                            className="text-[#4F4F4F] text-[14px] nunito-regular"
                          >
                            $ 301-400
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="amount5"
                            className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                            checked={amountRanges.range5}
                            onChange={() => handleAmountChange("range5")}
                          />
                          <label
                            htmlFor="amount5"
                            className="text-[#4F4F4F] text-[14px] nunito-regular"
                          >
                            $ 401-500
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="amount6"
                            className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                            checked={amountRanges.range6}
                            onChange={() => handleAmountChange("range6")}
                          />
                          <label
                            htmlFor="amount6"
                            className="text-[#4F4F4F] text-[14px] nunito-regular"
                          >
                            $ 501+
                          </label>
                        </div>
                      </div>

                      <div className="mb-4">
                        <div className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            id="custom"
                            className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                            checked={amountRanges.custom}
                            onChange={() => handleAmountChange("custom")}
                          />
                          <label
                            htmlFor="custom"
                            className="text-[#000000] text-[14px] nunito-regular"
                          >
                            Custom
                          </label>
                        </div>

                        {amountRanges.custom && (
                          <div className="flex gap-2">
                            <div className="flex-1">
                              <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                                Min
                              </label>
                              <input
                                type="text"
                                placeholder="Enter Amount"
                                value={customMin}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/[^0-9]/g, "");
                                  setCustomMin(value);
                                  // Validate max when min changes
                                  if (customMax) {
                                    setCustomMaxError(validateMaxValue(value, customMax));
                                  }
                                }}
                                className="w-full h-[49px] p-2 border border-[#D9D9D9] rounded-[8px] text-[14px] text-[#404040]"
                              />
                            </div>
                            <div className="flex-1">
                              <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                                Max
                              </label>
                              <input
                                type="text"
                                placeholder="Enter Amount"
                                value={customMax}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/[^0-9]/g, "");
                                  setCustomMax(value);
                                  setCustomMaxError(validateMaxValue(customMin, value));
                                }}
                                className={`w-full h-[49px] p-2 border rounded-[8px] text-[14px] text-[#404040] ${
                                  customMaxError ? 'border-red-500' : 'border-[#D9D9D9]'
                                }`}
                              />
                              {customMaxError && (
                                <p className="text-red-500 text-[12px] mt-1">{customMaxError}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  <div className="border-b mb-5 border-[#DFDFDF]"></div>
                </div>
              )}
              {/* Sales Filters */}
              {hasSalesFilters && (
                <>
                  {/* Date Range Filter */}
                  <div className="mb-6">
                    <div
                      className="flex justify-between items-center mb-3 cursor-pointer"
                      onClick={() =>
                        setSections({ ...sections, salesDateRange: !sections.salesDateRange })
                      }
                    >
                      <h3 className="text-[#404040] text-[16px] nunito-semibold">
                        Purchased Date Range
                      </h3>
                      {sections.salesDateRange ? (
                        <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                      ) : (
                        <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                      )}
                    </div>
                    {sections.salesDateRange && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[#404040] text-[14px] nunito-semibold mb-1">
                            From
                          </p>
                          <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            max={new Date().toLocaleDateString('en-CA')}
                            className="w-full p-3 border border-[#D9D9D9] rounded-[8px] text-[#404040] text-[14px] nunito-normal"
                          />
                        </div>
                        <div>
                          <p className="text-[#404040] text-[14px] nunito-semibold mb-1">
                            To
                          </p>
                          <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                             max={new Date().toLocaleDateString('en-CA')}
                            min={dateFrom}
                            className="w-full p-3 border border-[#D9D9D9] rounded-[8px] text-[#404040] text-[14px] nunito-normal"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="border-b mb-5 border-[#DFDFDF]"></div>
                  {/* Rating Filter */}
                  <div className="mb-6">
                    <div
                      className="flex justify-between items-center mb-3 cursor-pointer"
                      onClick={() =>
                        setSections({ ...sections, salesRating: !sections.salesRating })
                      }
                    >
                      <h3 className="text-[#404040] text-[16px] nunito-semibold">
                        Ratings
                      </h3>
                      {sections.salesRating ? (
                        <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                      ) : (
                        <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                      )}
                    </div>
                    {sections.salesRating && (
                      <select
                        value={selectedRating}
                        onChange={(e) => setSelectedRating(e.target.value)}
                        className="w-full p-3 border border-[#D9D9D9] rounded-[8px] text-[#404040] text-[14px] nunito-normal"
                      >
                        <option value="">All Ratings</option>
                        <option value="1">1 Star</option>
                        <option value="2">2 Stars</option>
                        <option value="3">3 Stars</option>
                        <option value="4">4 Stars</option>
                        <option value="5">5 Stars</option>
                      </select>
                    )}
                  </div>
                  <div className="border-b mb-5 border-[#DFDFDF]"></div>
                  {/* Organization Filter */}
                  <div className="mb-6 ">
                    <div
                      className="flex justify-between items-center mb-3 cursor-pointer"
                      onClick={() =>
                        setSections({ ...sections, salesOrganization: !sections.salesOrganization })
                      }
                    >
                      <h3 className="text-[#404040] text-[16px] nunito-semibold">
                        Organization Name
                      </h3>
                      {sections.salesOrganization ? (
                        <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                      ) : (
                        <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                      )}
                    </div>
                    {sections.salesOrganization && (
                      <div className="relative  " id="organization-dropdown">
                        <div
                          className="w-full p-3  border border-[#D9D9D9] rounded-[8px] text-[#404040] text-[14px] nunito-normal cursor-pointer bg-white"
                          onClick={() => setOrganizationDropdownOpen(!organizationDropdownOpen)}
                        >
                          {internalSelectedOrganizations.length === 0
                            ? "Select Organizations"
                            : internalSelectedOrganizations.join(", ")}
                        </div>
                        {organizationDropdownOpen && (
                          <div className="absolute left-0 mt-2    w-full bg-white border border-[#EBEBEB] rounded-lg shadow-lg z-50 py-2 max-h-48 overflow-y-auto">
                            {organizationOptions.map((org) => (
                              <label key={org.value} className="flex items-center px-3 py-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={internalSelectedOrganizations.includes(org.value)}
                                  onChange={() => handleOrganizationCheckbox(org.value)}
                                  className="mr-2 accent-[#F15D18]"
                                />
                                {org.label}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                  </div>
                    <div className="border-b mb-5 border-[#DFDFDF]"></div>
                </>
              )}
              {/* Package Tooltip Filters */}
              {hasPackageTooltipFilters && (
                <>
                  {/* Created Date Range Filter */}
                  <div className="mb-6">
                    <div
                      className="flex justify-between items-center mb-3 cursor-pointer"
                      onClick={() =>
                        setSections({ ...sections, createdDate: !sections.createdDate })
                      }
                    >
                      <h3 className="text-[#404040] text-[16px] nunito-semibold">
                        Created Date Range
                      </h3>
                      {sections.createdDate ? (
                        <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                      ) : (
                        <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                      )}
                    </div>
                    {sections.createdDate && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[#404040] text-[14px] nunito-semibold mb-1">
                            From
                          </p>
                          <input
                            type="date"
                            value={createdDateFrom}
                            onChange={(e) => setCreatedDateFrom(e.target.value)}
                            max={new Date().toLocaleDateString('en-CA')}
                            className="w-full p-3 border border-[#D9D9D9] rounded-[8px] text-[#404040] text-[14px] nunito-normal"
                          />
                        </div>
                        <div>
                          <p className="text-[#404040] text-[14px] nunito-semibold mb-1">
                            To
                          </p>
                          <input
                            type="date"
                            value={createdDateTo}
                            onChange={(e) => setCreatedDateTo(e.target.value)}
                             max={new Date().toLocaleDateString('en-CA')}
                            min={createdDateFrom}
                            className="w-full p-3 border border-[#D9D9D9] rounded-[8px] text-[#404040] text-[14px] nunito-normal"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="border-b mb-5 border-[#DFDFDF]"></div>
                  {/* Last Sale Date Range Filter */}
                  <div className="mb-6">
                    <div
                      className="flex justify-between items-center mb-3 cursor-pointer"
                      onClick={() =>
                        setSections({ ...sections, lastSaleDate: !sections.lastSaleDate })
                      }
                    >
                      <h3 className="text-[#404040] text-[16px] nunito-semibold">
                        Last Sale Date Range
                      </h3>
                      {sections.lastSaleDate ? (
                        <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                      ) : (
                        <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                      )}
                    </div>
                    {sections.lastSaleDate && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[#404040] text-[14px] nunito-semibold mb-1">
                            From
                          </p>
                          <input
                            type="date"
                            value={lastSaleDateFrom}
                            onChange={(e) => setLastSaleDateFrom(e.target.value)}
                            max={new Date().toLocaleDateString('en-CA')}
                            className="w-full p-3 border border-[#D9D9D9] rounded-[8px] text-[#404040] text-[14px] nunito-normal"
                          />
                        </div>
                        <div>
                          <p className="text-[#404040] text-[14px] nunito-semibold mb-1">
                            To
                          </p>
                          <input
                            type="date"
                            value={lastSaleDateTo}
                            onChange={(e) => setLastSaleDateTo(e.target.value)}
                             max={new Date().toLocaleDateString('en-CA')}
                            min={lastSaleDateFrom}
                            className="w-full p-3 border border-[#D9D9D9] rounded-[8px] text-[#404040] text-[14px] nunito-normal"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="border-b mb-5 border-[#DFDFDF]"></div>
                  {/* Ratings Filter */}
                  <div className="mb-6">
                    <div
                      className="flex justify-between items-center mb-3 cursor-pointer"
                      onClick={() =>
                        setSections({ ...sections, packageTooltipRating: !sections.packageTooltipRating })
                      }
                    >
                      <h3 className="text-[#404040] text-[16px] nunito-semibold">
                        Ratings
                      </h3>
                      {sections.packageTooltipRating ? (
                        <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                      ) : (
                        <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                      )}
                    </div>
                    {sections.packageTooltipRating && (
                      <select
                        value={packageTooltipRating}
                        onChange={(e) => setPackageTooltipRating(e.target.value)}
                        className="w-full p-3 border border-[#D9D9D9] rounded-[8px] text-[#404040] text-[14px] nunito-normal"
                      >
                        <option value="">All Ratings</option>
                        <option value="1">1 Star</option>
                        <option value="2">2 Stars</option>
                        <option value="3">3 Stars</option>
                        <option value="4">4 Stars</option>
                        <option value="5">5 Stars</option>
                      </select>
                    )}
                  </div>
                  <div className="border-b mb-5 border-[#DFDFDF]"></div>
                  {/* Sales Filter */}
                  <div className="mb-6">
                    <div
                      className="flex justify-between items-center mb-3 cursor-pointer"
                      onClick={() =>
                        setSections({ ...sections, sales: !sections.sales })
                      }
                    >
                      <h3 className="text-[#404040] text-[16px] nunito-semibold">
                        Sales
                      </h3>
                      {sections.sales ? (
                        <ChevronUp className="text-[#404040] w-[24px] h-[24px]" />
                      ) : (
                        <ChevronDown className="text-[#404040] w-[24px] h-[24px]" />
                      )}
                    </div>
                    {sections.sales && (
                      <>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="sales1"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={salesRanges.range1}
                              onChange={() => handleSalesChange("range1")}
                            />
                            <label
                              htmlFor="sales1"
                              className="text-[#4F4F4F] text-[14px] nunito-regular"
                            >
                              0-10
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="sales2"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={salesRanges.range2}
                              onChange={() => handleSalesChange("range2")}
                            />
                            <label
                              htmlFor="sales2"
                              className="text-[#4F4F4F] text-[14px] nunito-regular"
                            >
                              11-20
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="sales3"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={salesRanges.range3}
                              onChange={() => handleSalesChange("range3")}
                            />
                            <label
                              htmlFor="sales3"
                              className="text-[#4F4F4F] text-[14px] nunito-regular"
                            >
                              21-50
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="sales4"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={salesRanges.range4}
                              onChange={() => handleSalesChange("range4")}
                            />
                            <label
                              htmlFor="sales4"
                              className="text-[#4F4F4F] text-[14px] nunito-regular"
                            >
                              51-100
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="sales5"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={salesRanges.range5}
                              onChange={() => handleSalesChange("range5")}
                            />
                            <label
                              htmlFor="sales5"
                              className="text-[#4F4F4F] text-[14px] nunito-regular"
                            >
                              200+
                            </label>
                          </div>
                        </div>

                        <div className="mb-4">
                          <div className="flex items-center mb-2">
                            <input
                              type="checkbox"
                              id="salesCustom"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={salesRanges.custom}
                              onChange={() => handleSalesChange("custom")}
                            />
                            <label
                              htmlFor="salesCustom"
                              className="text-[#000000] text-[14px] nunito-regular"
                            >
                              Custom
                            </label>
                          </div>

                          {salesRanges.custom && (
                            <div className="flex gap-2">
                              <div className="flex-1">
                                <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                                  Min
                                </label>
                                <input
                                  type="text"
                                  placeholder="Enter Sales"
                                  value={salesCustomMin}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9]/g, "");
                                    setSalesCustomMin(value);
                                    // Validate max when min changes
                                    if (salesCustomMax) {
                                      setSalesCustomMaxError(validateMaxValue(value, salesCustomMax));
                                    }
                                  }}
                                  className="w-full h-[49px] p-2 border border-[#D9D9D9] rounded-[8px] text-[14px] text-[#404040]"
                                />
                              </div>
                              <div className="flex-1">
                                <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                                  Max
                                </label>
                                <input
                                  type="text"
                                  placeholder="Enter Sales"
                                  value={salesCustomMax}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9]/g, "");
                                    setSalesCustomMax(value);
                                    setSalesCustomMaxError(validateMaxValue(salesCustomMin, value));
                                  }}
                                  className={`w-full h-[49px] p-2 border rounded-[8px] text-[14px] text-[#404040] ${
                                    salesCustomMaxError ? 'border-red-500' : 'border-[#D9D9D9]'
                                  }`}
                                />
                                {salesCustomMaxError && (
                                  <p className="text-red-500 text-[12px] mt-1">{salesCustomMaxError}</p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="border-b mb-5 border-[#DFDFDF]"></div>
                  {/* Earnings Filter */}
                  <div className="mb-6">
                    <div
                      className="flex justify-between items-center mb-3 cursor-pointer"
                      onClick={() =>
                        setSections({ ...sections, earnings: !sections.earnings })
                      }
                    >
                      <h3 className="text-[#404040] text-[16px] nunito-semibold">
                        Earnings
                      </h3>
                      {sections.earnings ? (
                        <ChevronUp className="text-[#404040] w-[24px] h-[24px]" />
                      ) : (
                        <ChevronDown className="text-[#404040] w-[24px] h-[24px]" />
                      )}
                    </div>
                    {sections.earnings && (
                      <>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="earnings1"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={earningsRanges.range1}
                              onChange={() => handleEarningsChange("range1")}
                            />
                            <label
                              htmlFor="earnings1"
                              className="text-[#4F4F4F] text-[14px] nunito-regular"
                            >
                              $ 0-100
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="earnings2"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={earningsRanges.range2}
                              onChange={() => handleEarningsChange("range2")}
                            />
                            <label
                              htmlFor="earnings2"
                              className="text-[#4F4F4F] text-[14px] nunito-regular"
                            >
                              $ 101-200
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="earnings3"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={earningsRanges.range3}
                              onChange={() => handleEarningsChange("range3")}
                            />
                            <label
                              htmlFor="earnings3"
                              className="text-[#4F4F4F] text-[14px] nunito-regular"
                            >
                              $ 201-300
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="earnings4"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={earningsRanges.range4}
                              onChange={() => handleEarningsChange("range4")}
                            />
                            <label
                              htmlFor="earnings4"
                              className="text-[#4F4F4F] text-[14px] nunito-regular"
                            >
                              $ 301-400
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="earnings5"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={earningsRanges.range5}
                              onChange={() => handleEarningsChange("range5")}
                            />
                            <label
                              htmlFor="earnings5"
                              className="text-[#4F4F4F] text-[14px] nunito-regular"
                            >
                              $ 401-500
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="earnings6"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={earningsRanges.range6}
                              onChange={() => handleEarningsChange("range6")}
                            />
                            <label
                              htmlFor="earnings6"
                              className="text-[#4F4F4F] text-[14px] nunito-regular"
                            >
                              $ 501+
                            </label>
                          </div>
                        </div>

                        <div className="mb-4">
                          <div className="flex items-center mb-2">
                            <input
                              type="checkbox"
                              id="earningsCustom"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={earningsRanges.custom}
                              onChange={() => handleEarningsChange("custom")}
                            />
                            <label
                              htmlFor="earningsCustom"
                              className="text-[#000000] text-[14px] nunito-regular"
                            >
                              Custom
                            </label>
                          </div>

                          {earningsRanges.custom && (
                            <div className="flex gap-2">
                              <div className="flex-1">
                                <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                                  Min
                                </label>
                                <input
                                  type="text"
                                  placeholder="Enter Amount"
                                  value={earningsCustomMin}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9]/g, "");
                                    setEarningsCustomMin(value);
                                    // Validate max when min changes
                                    if (earningsCustomMax) {
                                      setEarningsCustomMaxError(validateMaxValue(value, earningsCustomMax));
                                    }
                                  }}
                                  className="w-full h-[49px] p-2 border border-[#D9D9D9] rounded-[8px] text-[14px] text-[#404040]"
                                />
                              </div>
                              <div className="flex-1">
                                <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                                  Max
                                </label>
                                <input
                                  type="text"
                                  placeholder="Enter Amount"
                                  value={earningsCustomMax}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9]/g, "");
                                    setEarningsCustomMax(value);
                                    setEarningsCustomMaxError(validateMaxValue(earningsCustomMin, value));
                                  }}
                                  className={`w-full h-[49px] p-2 border rounded-[8px] text-[14px] text-[#404040] ${
                                    earningsCustomMaxError ? 'border-red-500' : 'border-[#D9D9D9]'
                                  }`}
                                />
                                {earningsCustomMaxError && (
                                  <p className="text-red-500 text-[12px] mt-1">{earningsCustomMaxError}</p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="border-b mb-5 border-[#DFDFDF]"></div>{" "}
                </>
              )}
              {/* Purchases Tooltip Filters */}
              {hasPurchasesTooltipFilter && (
                <>
                  {/* Amount Filter for Purchases */}
                  <div className="mb-6">
                    <div
                      className="flex justify-between items-center mb-3 cursor-pointer"
                      onClick={() =>
                        setSections({ ...sections, amount: !sections.amount })
                      }
                    >
                      <h3 className="text-[#404040] text-[16px] nunito-semibold">
                        Amount
                      </h3>
                      {sections.amount ? (
                        <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                      ) : (
                        <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                      )}
                    </div>
                    {sections.amount && (
                      <>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="purchases-amount1"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={amountRanges.range1}
                              onChange={() => handleAmountChange("range1")}
                            />
                            <label
                              htmlFor="purchases-amount1"
                              className="text-[#4F4F4F] text-[14px] nunito-regular"
                            >
                              $ 0-10
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="purchases-amount2"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={amountRanges.range2}
                              onChange={() => handleAmountChange("range2")}
                            />
                            <label
                              htmlFor="purchases-amount2"
                              className="text-[#4F4F4F] text-[14px] nunito-regular"
                            >
                              $ 11-25
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="purchases-amount3"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={amountRanges.range3}
                              onChange={() => handleAmountChange("range3")}
                            />
                            <label
                              htmlFor="purchases-amount3"
                              className="text-[#4F4F4F] text-[14px] nunito-regular"
                            >
                              $ 26-50
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="purchases-amount4"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={amountRanges.range4}
                              onChange={() => handleAmountChange("range4")}
                            />
                            <label
                              htmlFor="purchases-amount4"
                              className="text-[#4F4F4F] text-[14px] nunito-regular"
                            >
                              $ 51-100
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="purchases-amount5"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={amountRanges.range5}
                              onChange={() => handleAmountChange("range5")}
                            />
                            <label
                              htmlFor="purchases-amount5"
                              className="text-[#4F4F4F] text-[14px] nunito-regular"
                            >
                              $ 101-500
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="purchases-amount6"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={amountRanges.range6}
                              onChange={() => handleAmountChange("range6")}
                            />
                            <label
                              htmlFor="purchases-amount6"
                              className="text-[#4F4F4F] text-[14px] nunito-regular"
                            >
                              $ 501+
                            </label>
                          </div>
                        </div>

                        <div className="mb-4">
                          <div className="flex items-center mb-2">
                            <input
                              type="checkbox"
                              id="purchases-custom"
                              className="h-[16px] w-[16px] mr-2 accent-[#F15D18]"
                              checked={amountRanges.custom}
                              onChange={() => handleAmountChange("custom")}
                            />
                            <label
                              htmlFor="purchases-custom"
                              className="text-[#000000] text-[14px] nunito-regular"
                            >
                              Custom
                            </label>
                          </div>

                          {amountRanges.custom && (
                            <div className="flex gap-2">
                              <div className="flex-1">
                                <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                                  Min
                                </label>
                                <input
                                  type="text"
                                  placeholder="Enter Amount"
                                  value={customMin}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9]/g, "");
                                    setCustomMin(value);
                                    // Validate max when min changes
                                    if (customMax) {
                                      setCustomMaxError(validateMaxValue(value, customMax));
                                    }
                                  }}
                                  className="w-full h-[49px] p-2 border border-[#D9D9D9] rounded-[8px] text-[14px] text-[#404040]"
                                />
                              </div>
                              <div className="flex-1">
                                <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                                  Max
                                </label>
                                <input
                                  type="text"
                                  placeholder="Enter Amount"
                                  value={customMax}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9]/g, "");
                                    setCustomMax(value);
                                    setCustomMaxError(validateMaxValue(customMin, value));
                                  }}
                                  className={`w-full h-[49px] p-2 border rounded-[8px] text-[14px] text-[#404040] ${
                                    customMaxError ? 'border-red-500' : 'border-[#D9D9D9]'
                                  }`}
                                />
                                {customMaxError && (
                                  <p className="text-red-500 text-[12px] mt-1">{customMaxError}</p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="border-b mb-5 border-[#DFDFDF]"></div>
                  {/* Ratings Filter for Purchases */}
                  <div className="mb-6">
                    <div
                      className="flex justify-between items-center mb-3 cursor-pointer"
                      onClick={() =>
                        setSections({ ...sections, purchasesRating: !sections.purchasesRating })
                      }
                    >
                      <h3 className="text-[#404040] text-[16px] nunito-semibold">
                        Rating
                      </h3>
                      {sections.purchasesRating ? (
                        <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                      ) : (
                        <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                      )}
                    </div>
                    {sections.purchasesRating && (
                      <select
                        value={purchasesRating}
                        onChange={(e) => setPurchasesRating(e.target.value)}
                        className="w-full p-3 border border-[#D9D9D9] rounded-[8px] text-[#404040] text-[14px] nunito-normal"
                      >
                        <option value="">All Ratings</option>
                        <option value="5">5 Stars</option>
                        <option value="4">4 Stars</option>
                        <option value="3">3 Stars</option>
                        <option value="2">2 Stars</option>
                        <option value="1">1 Star</option>
                      </select>
                    )}
                  </div>
                  <div className="border-b mb-5 border-[#DFDFDF]"></div>
                </>
              )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 mt-[10px] ">
                <button
                  className="p-3 w-[143px] h-[48px] border border-[#F15D18] text-[#F15D18] text-[16px] nunito-medium rounded-[8px]"
                  onClick={() => {
                    // Revert to last applied filter values
                    setAmountRanges(lastAppliedFiltersRef.current.amountRanges);
                    setStatus(lastAppliedFiltersRef.current.status);
                    setExpertRating(lastAppliedFiltersRef.current.expertRating);
                    setCustomerRating(lastAppliedFiltersRef.current.customerRating);
                    setCustomMin(lastAppliedFiltersRef.current.customMin);
                    setCustomMax(lastAppliedFiltersRef.current.customMax);
                    setCustomMaxError("");
                    setSelectedOrganization(
                      lastAppliedFiltersRef.current.selectedOrganization
                    );
                    setSelectedRole(lastAppliedFiltersRef.current.selectedRole);
                    setWalletRanges(lastAppliedFiltersRef.current.walletRanges);
                    setWalletCustomMin(lastAppliedFiltersRef.current.walletCustomMin);
                    setWalletCustomMax(lastAppliedFiltersRef.current.walletCustomMax);
                    setWalletCustomMaxError("");
                    // Sales filters
                    setInternalSelectedOrganizations(lastAppliedFiltersRef.current.selectedOrganizations);
                    setDateFrom(lastAppliedFiltersRef.current.dateFrom);
                    setDateTo(lastAppliedFiltersRef.current.dateTo);
                    setSelectedRating(lastAppliedFiltersRef.current.selectedRating);
                    // Purchases filters
                    setPurchasesRating(lastAppliedFiltersRef.current.purchasesRating);
                    // Package tooltip filters
                    setCreatedDateFrom(lastAppliedFiltersRef.current.createdDateFrom);
                    setCreatedDateTo(lastAppliedFiltersRef.current.createdDateTo);
                    setLastSaleDateFrom(lastAppliedFiltersRef.current.lastSaleDateFrom);
                    setLastSaleDateTo(lastAppliedFiltersRef.current.lastSaleDateTo);
                    setPackageTooltipRating(lastAppliedFiltersRef.current.packageTooltipRating);
                    setSalesRanges(lastAppliedFiltersRef.current.salesRanges);
                    setSalesCustomMin(lastAppliedFiltersRef.current.salesCustomMin);
                    setSalesCustomMax(lastAppliedFiltersRef.current.salesCustomMax);
                    setSalesCustomMaxError("");
                    setEarningsRanges(lastAppliedFiltersRef.current.earningsRanges);
                    setEarningsCustomMin(lastAppliedFiltersRef.current.earningsCustomMin);
                    setEarningsCustomMax(lastAppliedFiltersRef.current.earningsCustomMax);
                    setEarningsCustomMaxError("");
                    
                    // Call individual callbacks to update parent state
                    if (hasSalesFilters) {
                      if (onOrganizationChange) {
                        onOrganizationChange(lastAppliedFiltersRef.current.selectedOrganizations);
                      }
                      if (onDateRangeChange) {
                        onDateRangeChange({ from: lastAppliedFiltersRef.current.dateFrom, to: lastAppliedFiltersRef.current.dateTo });
                      }
                      if (onRatingChange) {
                        onRatingChange(lastAppliedFiltersRef.current.selectedRating);
                      }
                    }
                    
                    setShowFilter(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  className={`p-3 w-[201px] h-[48px] text-[16px] nunito-semibold rounded-[8px] ${
                    hasValidationErrors()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#F15D18] text-white'
                  }`}
                  onClick={applyFilters}
                  disabled={hasValidationErrors()}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
